/**
 * Titelmallar med » enligt mönstret på Elmarknad.se i Google:
 *
 *   Elpris idag per kWh » Dagens elpris på Nord Pool
 *   Elpriser i Vara » Elavtal & spotpriser
 *   Elpriser 2023 » Snittpriser på el, månad för månad
 *
 * En mall består av ett sökvägsmönster (glob med `*`) och en modifierare som
 * läggs efter ». Längsta matchande mönster vinner. Varumärket läggs inte till
 * i dessa titlar – Google visar redan "Elmarknad.se" som sajtnamn.
 */

export interface TitleTemplate {
  /** Sökvägsmönster, `*` matchar ett segment, `**` matchar flera. */
  pattern: string;
  /** Text efter » (modifierare). */
  modifier: string;
  /** Egen bas istället för sidans etikett (stödjer %label%). */
  base?: string;
}

export const TITLE_SEPARATOR = "»";

/** Ordnas fritt – matchningen väljer alltid det mest specifika mönstret. */
export const TITLE_TEMPLATES: TitleTemplate[] = [
  // Elpriser
  { pattern: "/elpriser", modifier: "Aktuella elpriser per kWh i Sverige" },
  { pattern: "/elpriser/idag", base: "Elpris idag per kWh", modifier: "Dagens elpris på Nord Pool" },
  { pattern: "/elpriser/imorgon", base: "Elpris imorgon per kWh", modifier: "Morgondagens elpris" },
  { pattern: "/elpriser/prognos", base: "Elprisprognos", modifier: "AI-prognos för elpriser & spotpris | Elmarknad.se" },
  { pattern: "/elpriser/jamfor", base: "Jämför elpriser", modifier: "Elpriser per elområde & kommun" },
  { pattern: "/elpriser/historik", base: "Historiska elpriser", modifier: "Snittpriser år för år" },
  { pattern: "/elpriser/historik/*", modifier: "Snittpriser på el, månad för månad" },
  { pattern: "/elpriser/historik/*/*", modifier: "Snittpris på el per dag" },
  { pattern: "/elpriser/elomrade-*", base: "Elpriser i %label_lc%", modifier: "Spotpris per timme & kvart" },
  // Kommunsidor: /elpriser/vara
  { pattern: "/elpriser/*", base: "Elpriser i %label%", modifier: "Spotpris idag & imorgon | Elmarknad.se" },

  // Spotpriser
  { pattern: "/spotpris", modifier: "Spotpriset på el per kWh" },
  { pattern: "/spotpris/idag", base: "Spotpris idag", modifier: "Dagens spotpris på Nord Pool" },
  { pattern: "/spotpris/imorgon", base: "Spotpris imorgon", modifier: "Morgondagens spotpris" },
  { pattern: "/spotpris/elomrade-*", modifier: "Elpris per timme & kvart i elområdet" },

  // Elavtal
  // Ingen siffra här: antalet elbolag räknas fram ur avtalsflödet (src/lib/siteStats.ts)
  // och en hårdkodad siffra blir inaktuell ("150 elbolag" stämde inte).
  { pattern: "/elavtal", modifier: "Jämför elavtal från Sveriges elbolag" },
  { pattern: "/elavtal/billiga", modifier: "Elavtal med billig el | Elmarknad.se" },
  { pattern: "/elavtal/jamfor", base: "Jämför elavtal", modifier: "Hitta billigaste elavtalet" },
  { pattern: "/elavtal/*", modifier: "Jämför priser & villkor" },
  { pattern: "/elavtal/fast", base: "Fast elavtal", modifier: "Elavtal med fast pris | Elmarknad.se" },
  { pattern: "/elavtal/rorligt", base: "Rörligt elavtal", modifier: "Elavtal med rörligt pris | Elmarknad.se" },
  { pattern: "/elavtal/manadspris", base: "Månadsprisavtal", modifier: "Elavtal med månadspris | Elmarknad.se" },
  { pattern: "/elavtal/kvartspris", base: "Kvartsprisavtal", modifier: "Elavtal med kvartspris | Elmarknad.se" },
  { pattern: "/elavtal/timpris", base: "Timprisavtal", modifier: "Elavtal med timpris | Elmarknad.se" },
  { pattern: "/elavtal/mixpris", base: "Mixavtal", modifier: "Elavtal med mixpris | Elmarknad.se" },
  { pattern: "/elavtal/tillsvidare", base: "Tillsvidareavtal", modifier: "Elavtal med tillsvidarepris | Elmarknad.se" },
  { pattern: "/elavtal/anvisat", base: "Anvisat elavtal", modifier: "Elavtal med anvisat pris | Elmarknad.se" },

  // Elbolag
  { pattern: "/elbolag", modifier: "Jämför elbolag i Sverige" },
  { pattern: "/elbolag/*", modifier: "Priser, villkor & omdömen" },
  { pattern: "/elhandel", base: "Elhandelsbolag", modifier: "Alla elbolag i Sverige" },
  { pattern: "/elbolag/billigaste", base: "Billiga elbolag", modifier: "Elbolag med billigast el | Elmarknad.se" },
  { pattern: "/elhandel/*", modifier: "Elavtal, priser & omdömen" },
  { pattern: "/elnat", base: "Elnätsbolag", modifier: "Nätavgifter & elnätsområden" },
  { pattern: "/elnat/*", modifier: "Nätavgifter & elområde" },

  // Innehåll
  { pattern: "/nyheter/*", modifier: "Nyheter om elmarknaden" },
  { pattern: "/rapporter/*", modifier: "Månadsrapport om elpriserna" },
  { pattern: "/ordlista/*", modifier: "Ordlista för el & energi" },
  { pattern: "/blogg/*", modifier: "Guider om el & energi" },
];

const cache = new Map<string, TitleTemplate | null>();

function toRegExp(pattern: string): RegExp {
  const body = pattern
    .split("/")
    .map((seg) =>
      seg
        .split("*")
        .map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
        .join("[^/]*"),
    )
    .join("/");
  return new RegExp(`^${body}$`);
}

/** Hittar den mest specifika titelmallen för en sökväg. */
export function findTitleTemplate(path: string): TitleTemplate | null {
  const key = path;
  if (cache.has(key)) return cache.get(key)!;
  let best: TitleTemplate | null = null;
  let bestScore = -1;
  for (const t of TITLE_TEMPLATES) {
    if (!toRegExp(t.pattern).test(path)) continue;
    // Fler segment och färre wildcards = mer specifikt.
    const segments = t.pattern.split("/").length;
    const wildcards = (t.pattern.match(/\*/g) ?? []).length;
    // Fler bokstäver utanför wildcards = mer specifikt mönster.
    const literals = t.pattern.replace(/\*/g, "").length;
    const score = segments * 1000 - wildcards * 100 + literals;
    if (score > bestScore) {
      bestScore = score;
      best = t;
    }
  }
  cache.set(key, best);
  return best;
}

/**
 * Bygger en titel enligt » -mallen för sökvägen.
 * Returnerar null när ingen mall matchar (då används standardmallen med
 * varumärkessuffix i seoTitles.ts).
 */
export function templateTitle(path: string, label: string): string | null {
  const template = findTitleTemplate(path);
  if (!template) return null;
  const lower = label.charAt(0).toLowerCase() + label.slice(1);
  let base = (template.base ?? "%label%")
    .replace(/%label_lc%/g, lower)
    .replace(/%label%/g, label)
    .trim();
  // Undvik dubblering när etiketten redan innehåller mallens prefix,
  // t.ex. base "Elpriser i %label%" med label "Elpriser i Stockholm".
  const prefix = (template.base ?? "%label%").split("%label")[0]?.trim();
  if (prefix && label.toLowerCase().startsWith(prefix.toLowerCase())) {
    base = label.trim();
  }
  if (!base) return null;
  return `${base} ${TITLE_SEPARATOR} ${template.modifier}`;
}
